/**
 * BundleScheduleModal (Iter 36.i) — configure (or pause / clear) the
 * weekly auto-send of the Steering-Committee Bundle PDF for the workspace.
 *
 * Design notes:
 *   - Recipients: list of plain emails. Stored as a comma/newline-separated
 *     textarea on the frontend, parsed/de-duped client-side, validated
 *     server-side by Pydantic EmailStr. We pick a list textarea (not the
 *     iter 36.h "dropdown OR email" pattern) because schedules are saved
 *     once and fired many times — a 5-email distribution list is the
 *     normal case, and a chip-input is overkill for that.
 *   - Day / Hour are user-friendly dropdowns; the saved value is UTC so
 *     we render the local-time conversion right under the input to avoid
 *     mid-meeting "wait, it's 9am UTC, what's that in my zone?" surprises.
 *   - "Last send" / "Next send" displayed when a schedule exists, so the
 *     consultant can verify the cron is alive without leaving the modal.
 *   - "Pause" is a single checkbox — not a toggle button — so the
 *     intentional "keep my recipients + schedule but stop firing" path
 *     is one click.
 *   - "Clear schedule" calls DELETE and removes the Mongo doc entirely,
 *     for when the engagement ends.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  XIcon as X,
  ClockClockwiseIcon as ClockClockwise,
  CalendarIcon as Calendar,
  CircleNotchIcon as Spinner,
  PauseIcon as Pause,
  TrashIcon as Trash,
  CheckCircleIcon as CheckCircle,
} from "@phosphor-icons/react";
import {
  deleteBundleSchedule,
  getBundleSchedule,
  putBundleSchedule,
} from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

const DAYS = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

// 24 dropdown options, formatted "01:00 UTC" / "13:00 UTC".
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${h.toString().padStart(2, "0")}:00 UTC`,
}));

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseRecipients(text) {
  return (text || "")
    .split(/[\n,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function localTimePreview(dayOfWeek, hourUtc) {
  // Construct a UTC ISO at next occurrence of (dayOfWeek, hourUtc) and let
  // the browser render it in the user's locale. We don't need real "next
  // run" here, just a representative example so the consultant doesn't
  // get tripped up by the UTC offset.
  const now = new Date();
  const sample = new Date(now);
  // Day-of-week constants on the JS side: Sun=0..Sat=6. Ours: Mon=0..Sun=6.
  // Convert: JS-day = (ourDay + 1) % 7.
  const targetJsDay = (dayOfWeek + 1) % 7;
  const diff = (targetJsDay - sample.getUTCDay() + 7) % 7;
  sample.setUTCDate(sample.getUTCDate() + diff);
  sample.setUTCHours(hourUtc, 0, 0, 0);
  return sample.toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function fmtIsoDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

export default function BundleScheduleModal({
  workspaceId,
  workspaceName,
  clientName,
  open,
  onClose,
  onSaved,
}) {
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [schedule, setSchedule] = useState(null);

  const [recipientsText, setRecipientsText] = useState("");
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [hourUtc, setHourUtc] = useState(9);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    setSubmitting(false);
    setDeleting(false);
    getBundleSchedule(workspaceId)
      .then((res) => {
        const sched = res.schedule;
        if (sched) {
          setSchedule(sched);
          setRecipientsText((sched.recipients || []).join("\n"));
          setDayOfWeek(sched.day_of_week ?? 0);
          setHourUtc(sched.hour_utc ?? 9);
          setPaused(!!sched.paused);
        } else {
          setSchedule(null);
          setRecipientsText("");
          setDayOfWeek(0);
          setHourUtc(9);
          setPaused(false);
        }
      })
      .catch(() => setSchedule(null))
      .finally(() => setLoaded(true));
  }, [open, workspaceId]);

  const parsed = useMemo(() => parseRecipients(recipientsText), [recipientsText]);
  const invalid = parsed.filter((e) => !EMAIL_RE.test(e));
  const canSave = !submitting && parsed.length > 0 && invalid.length === 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      const res = await putBundleSchedule(workspaceId, {
        recipients: parsed,
        day_of_week: dayOfWeek,
        hour_utc: hourUtc,
        paused,
      });
      toast.success(
        paused
          ? "Bundle schedule saved — paused. Resume any time."
          : `Bundle scheduled for ${DAYS[dayOfWeek].label}s at ${HOURS[hourUtc].label}.`,
      );
      setSchedule(res.schedule);
      onSaved?.(res.schedule);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to save schedule.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await deleteBundleSchedule(workspaceId);
      toast.success(res.deleted ? "Schedule cleared." : "No schedule to clear.");
      setSchedule(null);
      onSaved?.(null);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to clear schedule.");
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  const title = clientName || workspaceName;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="schedule-modal-backdrop"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white border border-[var(--ml-border)] max-w-xl w-full shadow-xl max-h-[90vh] overflow-y-auto"
        data-testid="schedule-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ml-border)] sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <ClockClockwise size={16} weight="bold" className="text-[var(--ml-primary)]" />
            <h3 className="font-semibold text-lg">Schedule weekly bundle</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="schedule-modal-close"
            className="ml-btn-icon text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm space-y-1">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Workspace</div>
            <div className="font-semibold" data-testid="schedule-modal-title">{title}</div>
            {clientName && clientName !== workspaceName && (
              <div className="text-xs text-[var(--ml-text-secondary)]">{workspaceName}</div>
            )}
          </div>

          {!loaded ? (
            <div className="flex items-center gap-2 text-sm text-[var(--ml-text-secondary)] py-6">
              <Spinner size={14} className="animate-spin" />
              Loading current schedule…
            </div>
          ) : (
            <>
              {schedule && (
                <div
                  className="ml-card p-3 bg-[var(--ml-surface)]/40 text-xs space-y-1"
                  data-testid="schedule-current-state"
                >
                  <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                    Current schedule
                  </div>
                  <div>
                    <strong>Last send:</strong>{" "}
                    <span data-testid="schedule-last-send">{fmtIsoDateTime(schedule.last_sent_at)}</span>
                    {schedule.last_send_status && (
                      <span className="ml-mono text-[10px] uppercase tracking-widest ml-2 px-1.5 py-0.5"
                        style={{
                          color: schedule.last_send_status === "ok" ? "#0e6027" :
                                 schedule.last_send_status === "partial" ? "#a56600" : "#a2191f",
                          background: schedule.last_send_status === "ok" ? "#defbe6" :
                                      schedule.last_send_status === "partial" ? "#fcf4d6" : "#fff1f1",
                        }}>
                        {schedule.last_send_status}
                      </span>
                    )}
                  </div>
                  <div>
                    <strong>Next send:</strong>{" "}
                    <span data-testid="schedule-next-send">{fmtIsoDateTime(schedule.next_run_at)}</span>
                  </div>
                  <div>
                    <strong>Total sends:</strong>{" "}
                    <span data-testid="schedule-send-count">{schedule.send_count || 0}</span>
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor="schedule-recipients"
                  className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1"
                >
                  Recipients <span className="opacity-60">(one per line or comma-separated)</span>
                </label>
                <textarea
                  id="schedule-recipients"
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  placeholder="exec@client.example.com&#10;pmo@client.example.com&#10;sponsor@client.example.com"
                  rows={5}
                  disabled={submitting}
                  className="ml-input w-full text-sm font-mono"
                  data-testid="schedule-recipients-input"
                />
                <div className="flex items-center justify-between mt-1 text-[11px]">
                  <span className="text-[var(--ml-text-secondary)] ml-mono" data-testid="schedule-recipients-count">
                    {parsed.length} valid email{parsed.length === 1 ? "" : "s"}
                  </span>
                  {invalid.length > 0 && (
                    <span className="text-[#a2191f]" data-testid="schedule-recipients-invalid">
                      Invalid: {invalid.slice(0, 3).join(", ")}
                      {invalid.length > 3 ? ` (+${invalid.length - 3} more)` : ""}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="schedule-day" className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1 flex items-center gap-1">
                    <Calendar size={11} weight="bold" /> Day of week
                  </label>
                  <select
                    id="schedule-day"
                    value={dayOfWeek}
                    onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))}
                    disabled={submitting}
                    className="ml-input w-full text-sm"
                    data-testid="schedule-day-select"
                  >
                    {DAYS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="schedule-hour" className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1 flex items-center gap-1">
                    <ClockClockwise size={11} weight="bold" /> Hour (UTC)
                  </label>
                  <select
                    id="schedule-hour"
                    value={hourUtc}
                    onChange={(e) => setHourUtc(parseInt(e.target.value, 10))}
                    disabled={submitting}
                    className="ml-input w-full text-sm"
                    data-testid="schedule-hour-select"
                  >
                    {HOURS.map((h) => (
                      <option key={h.value} value={h.value}>{h.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div
                className="text-[11px] text-[var(--ml-text-secondary)]"
                data-testid="schedule-local-preview"
              >
                Your local time: <strong>{localTimePreview(dayOfWeek, hourUtc)}</strong>
              </div>

              <label
                className="flex items-start gap-2 text-sm cursor-pointer"
                data-testid="schedule-paused-label"
              >
                <input
                  type="checkbox"
                  checked={paused}
                  onChange={(e) => setPaused(e.target.checked)}
                  disabled={submitting}
                  className="mt-1"
                  data-testid="schedule-paused-checkbox"
                />
                <span>
                  <span className="font-medium">Paused</span>
                  <span className="block text-xs text-[var(--ml-text-secondary)]">
                    Keep this schedule on file but skip the next runs. Untick to resume.
                  </span>
                </span>
              </label>

              <div className="text-xs text-[var(--ml-text-secondary)] ml-card p-3 bg-[var(--ml-surface)]/40 flex items-start gap-2">
                <CheckCircle size={14} weight="duotone" className="flex-shrink-0 mt-0.5 text-[var(--ml-primary)]" />
                <span>
                  At the scheduled time, the bundle PDF is composed fresh
                  from this workspace and emailed to every recipient. The
                  email body shows the same 3-card executive read-out as
                  the manual &ldquo;Email bundle&rdquo; action.
                </span>
              </div>
            </>
          )}

          <div className="flex justify-between gap-2 pt-2 border-t border-[var(--ml-border)] -mx-5 px-5 pt-4 sticky bottom-0 bg-white">
            {schedule ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting || submitting}
                data-testid="schedule-delete-btn"
                className="ml-btn-secondary inline-flex items-center gap-1.5 text-[#a2191f] border-[#a2191f]"
              >
                {deleting ? (
                  <><Spinner size={12} className="animate-spin" /> Clearing…</>
                ) : (
                  <><Trash size={12} weight="bold" /> Clear schedule</>
                )}
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="ml-btn-secondary"
                data-testid="schedule-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave || !loaded}
                data-testid="schedule-save-btn"
                className="ml-btn-primary"
                title={
                  parsed.length === 0
                    ? "Add at least one recipient email"
                    : invalid.length > 0
                    ? "Fix invalid emails first"
                    : "Save schedule"
                }
              >
                {submitting ? (
                  <><Spinner size={12} className="animate-spin" /> Saving…</>
                ) : paused ? (
                  <><Pause size={12} weight="bold" /> Save (paused)</>
                ) : (
                  <><CheckCircle size={12} weight="bold" /> Save schedule</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
